import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../utils/api";
import toast from "react-hot-toast";
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import compressCINImage from "../../utils/compressCINImage";

const VERIFY_DEBOUNCE_MS = 700;

const verifyCinPhoto = async (file, name, cin) => {
    const form = new FormData();
    form.append("recto", file);
    form.append("name", name);
    form.append("cin", cin);
    const { data } = await axiosInstance.post("/badge/verify-cin", form, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
};

const Field = ({ label, required, children }) => (
    <div>
        <label className="block text-gray-800 text-sm font-semibold mb-1.5">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        {children}
    </div>
);

const inputCls =
    "w-full bg-white border-2 border-gray-200 text-gray-900 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition placeholder-gray-400";

const PhotoTile = ({ label, file, inputId, onChange, onCamera }) => (
    <div className="flex flex-col gap-2">
        <label className="block text-gray-800 text-sm font-semibold">
            {label} <span className="text-red-500">*</span>
        </label>
        <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/*"
            onChange={onChange}
            className="hidden"
            id={inputId}
        />
        <label
            htmlFor={inputId}
            className={`flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-xl cursor-pointer transition select-none ${file
                ? "border-emerald-400 bg-emerald-50"
                : "border-gray-300 bg-gray-50 hover:bg-blue-50 hover:border-blue-300"
                }`}
        >
            {file ? (
                <div className="text-center px-2">
                    <svg className="w-8 h-8 text-emerald-500 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-emerald-700 text-xs font-semibold truncate max-w-[120px]">{file.name}</p>
                    <p className="text-emerald-500 text-xs mt-0.5">Tap to change</p>
                </div>
            ) : (
                <div className="text-center">
                    <svg className="w-8 h-8 text-gray-400 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <p className="text-gray-500 text-xs font-medium">Upload file</p>
                </div>
            )}
        </label>
        <button
            type="button"
            onClick={onCamera}
            className="flex items-center justify-center gap-1.5 bg-blue-900 hover:bg-blue-800 text-white text-xs font-semibold px-3 py-2 rounded-lg transition"
        >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Take Photo
        </button>
    </div>
);

const VerificationStatus = ({ verification, canRecheck, onRecheck }) => {
    const { status, cinMatch, nameMatch } = verification;

    let wrapperClass = "bg-slate-50 border-slate-200";
    let textClass = "text-slate-500";
    let text = "Waiting for verification...";
    let icon = (
        <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    );

    if (status === "checking") {
        wrapperClass = "bg-blue-50 border-blue-200";
        textClass = "text-blue-700";
        text = "Verifying CIN...";
        icon = <div className="w-4 h-4 border-2 border-blue-700 border-t-transparent rounded-full animate-spin shrink-0" />;
    } else if (status === "error") {
        wrapperClass = "bg-amber-50 border-amber-200";
        textClass = "text-amber-700";
        text = "Verification unavailable — try again";
        icon = (
            <svg className="w-4 h-4 text-amber-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        );
    } else if (status === "done") {
        if (cinMatch && nameMatch) {
            wrapperClass = "bg-emerald-50 border-emerald-200";
            textClass = "text-emerald-700";
            text = "Info verified against photo";
            icon = (
                <svg className="w-4 h-4 text-emerald-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
            );
        } else {
            wrapperClass = "bg-red-50 border-red-200";
            textClass = "text-red-600";
            text = !cinMatch && !nameMatch
                ? "Name and CIN don't match the photo"
                : !cinMatch
                    ? "CIN doesn't match the photo"
                    : "Name doesn't match the photo";
            icon = (
                <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            );
        }
    }

    return (
        <div className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl border transition-colors duration-300 ${wrapperClass}`}>
            <div className="flex items-center gap-2 min-w-0">
                {icon}
                <span className={`text-xs font-medium truncate ${textClass}`}>{text}</span>
            </div>
            {canRecheck && (
                <button type="button" onClick={onRecheck} className="shrink-0 text-xs font-semibold text-blue-900 hover:text-blue-700 transition px-2 py-1 rounded-lg hover:bg-white/60">
                    Recheck
                </button>
            )}
        </div>
    );
};

const Steps = ({ current }) => {
    const steps = ["Select Stands", "Fill Details"];

    return (
        <div className="flex items-center justify-center gap-0 mb-8">
            {steps.map((label, idx) => {
                const num = idx + 1;
                const active = num === current;
                const done = num < current;
                return (
                    <React.Fragment key={num}>
                        <div className="flex flex-col items-center">
                            <div
                                className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm border-2 transition-all ${done
                                    ? "bg-blue-900 border-blue-900 text-white"
                                    : active
                                        ? "bg-white border-blue-900 text-blue-900"
                                        : "bg-white border-gray-300 text-gray-400"
                                    }`}
                            >
                                {done ? (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : (
                                    num
                                )}
                            </div>
                            <span className={`text-xs mt-1.5 font-medium ${active ? "text-blue-900" : "text-gray-400"}`}>
                                {label}
                            </span>
                        </div>
                        {idx < steps.length - 1 && (
                            <div
                                className={`w-12 sm:w-20 h-0.5 mb-5 mx-1 transition-all ${num < current ? "bg-blue-900" : "bg-gray-200"
                                    }`}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

const REGISTRATION_CARD_ACCENTS = {
    blue: {
        card: "hover:border-blue-900 hover:bg-blue-50 focus-visible:ring-blue-900/40",
        iconBox: "bg-blue-100 group-hover:bg-blue-900",
        title: "group-hover:text-blue-900",
        cta: "text-blue-900 group-hover:text-blue-800",
    },
};

/** Landing: single New Registration entry point. */
const RegistrationOptionCard = ({ accent, title, description, ctaLabel, onClick, icon }) => {
    const a = REGISTRATION_CARD_ACCENTS[accent];
    return (
        <button
            type="button"
            onClick={onClick}
            className={`group flex flex-col items-center gap-4 p-6 rounded-2xl border-2 border-gray-200 bg-white transition text-left shadow-sm hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 min-h-[280px] ${a.card}`}
        >
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition shrink-0 ${a.iconBox}`}>{icon}</div>
            <div className="text-center flex-1 flex flex-col">
                <p className={`font-bold text-gray-900 text-base transition ${a.title}`}>{title}</p>
                <p className="text-gray-500 text-xs mt-1 leading-relaxed flex-1">{description}</p>
            </div>
            <span className={`mt-auto inline-flex items-center gap-1 text-xs font-semibold ${a.cta}`}>
                {ctaLabel}
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
            </span>
        </button>
    );
};

const PersonForm = ({ label, data, onChange, onPhoto, onCamera, personType, index, badgeType, verification, canRecheck, onRecheck }) => (<div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
    <div className="bg-blue-900 px-6 py-4 flex items-center justify-between">
        <h3 className="text-white font-bold text-base flex items-center gap-2">
            {personType === "standiste" ? (
                <span className="bg-amber-400 text-blue-900 text-xs font-bold px-2 py-0.5 rounded-full">Stand Manager</span>
            ) : (
                <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">#{index + 1}</span>
            )}
            {label}
        </h3>
        <span className="text-blue-200 text-xs font-medium">
            {badgeType.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
        </span>
    </div>

    <div className="p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
                <Field label="Full Name" required>
                    <input
                        type="text"
                        placeholder="Enter full name"
                        value={data.name}
                        onChange={(e) => onChange("name", e.target.value)}
                        className={inputCls}
                    />
                </Field>
            </div>
            <Field label="CIN" required>
                <input
                    type="text"
                    placeholder="CIN number"
                    value={data.cin}
                    onChange={(e) => onChange("cin", e.target.value)}
                    className={inputCls}
                />
            </Field>
            <Field label="Phone (WhatsApp)" required>
                <input
                    type="tel"
                    placeholder="2126XXXXXXXX"
                    value={data.phone}
                    onChange={(e) => onChange("phone", e.target.value)}
                    className={inputCls}
                />
            </Field>
        </div>

        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-slate-700 text-sm font-bold flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    </svg>
                    CIN Photos
                    <span className="text-red-500 font-normal text-xs">— both required</span>
                </p>
            </div>

            {verification && (
                <VerificationStatus verification={verification} canRecheck={canRecheck} onRecheck={onRecheck} />
            )}

            <div className="grid grid-cols-2 gap-4">
                <PhotoTile
                    label="Front (Recto)"
                    file={data.cinPhotos.recto}
                    inputId={`${personType}-${index ?? "s"}-recto`}
                    onChange={(e) => onPhoto("recto", e.target.files[0])}
                    onCamera={() => onCamera({ personType, index: index ?? null, side: "recto" })}
                />
                <PhotoTile
                    label="Back (Verso)"
                    file={data.cinPhotos.verso}
                    inputId={`${personType}-${index ?? "s"}-verso`}
                    onChange={(e) => onPhoto("verso", e.target.files[0])}
                    onCamera={() => onCamera({ personType, index: index ?? null, side: "verso" })}
                />
            </div>
        </div>
    </div>
</div>
);

const StandisteForm = () => {
    const { eventId } = useParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [mode, setMode] = useState(null);
    const [step, setStep] = useState(1);

    // Step 1 – stands
    const [availableStands, setAvailableStands] = useState([]);
    const [selectedStands, setSelectedStands] = useState([]);
    const [standSearch, setStandSearch] = useState("");

    const [badgeType] = useState("build_up");

    const [submitting, setSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const emptyPerson = () => ({
        name: "",
        phone: "",
        cin: "",
        cinPhotos: { recto: null, verso: null },
        verification: { status: "idle", cinMatch: null, nameMatch: null },
    });

    const [companyInfo, setCompanyInfo] = useState({
        companyName: "",
        chiefName: "",
        phone: "",
        email: "",
    });
    const [staffList] = useState([]);

    const [stream, setStream] = useState(null);
    const [cameraTarget, setCameraTarget] = useState(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [standiste, setStandiste] = useState(emptyPerson());
    const standisteRef = useRef(standiste);
    useEffect(() => { standisteRef.current = standiste; }, [standiste]);

    const verifyTimer = useRef(null);
    useEffect(() => () => clearTimeout(verifyTimer.current), []);
    const verifySeq = useRef(0);

    const runVerification = useCallback(async (file, name, cin) => {
        if (!file || !name || !cin) return;
        const mySeq = ++verifySeq.current;

        setStandiste((prev) => ({ ...prev, verification: { status: "checking", cinMatch: null, nameMatch: null } }));
        try {
            const result = await verifyCinPhoto(file, name, cin);
            if (mySeq !== verifySeq.current) return;
            setStandiste((prev) => ({
                ...prev,
                verification: { status: "done", cinMatch: result.cinMatch, nameMatch: result.nameMatch },
            }));
        } catch {
            if (mySeq !== verifySeq.current) return;
            setStandiste((prev) => ({ ...prev, verification: { status: "error", cinMatch: null, nameMatch: null } }));
            toast.error("CIN verification failed");
        }
    }, []);

    const scheduleVerification = useCallback(() => {
        clearTimeout(verifyTimer.current);
        verifyTimer.current = setTimeout(() => {
            const { name, cin, cinPhotos } = standisteRef.current;
            if (cinPhotos.recto && name.trim() && cin.trim()) {
                runVerification(cinPhotos.recto, name.trim(), cin.trim());
            }
        }, VERIFY_DEBOUNCE_MS);
    }, [runVerification]);

    const handleRecheck = useCallback(() => {
        clearTimeout(verifyTimer.current);
        const { name, cin, cinPhotos } = standisteRef.current;
        if (cinPhotos.recto && name.trim() && cin.trim()) {
            runVerification(cinPhotos.recto, name.trim(), cin.trim());
        }
    }, [runVerification]);

    useEffect(() => {
        const init = async () => {
            try {
                const standsRes = await axiosInstance.get(`/stand/${eventId}`);
                setAvailableStands(standsRes.data.stands || []);
            } catch (err) {
                setError("Failed to load stands for this event.");
            } finally {
                setLoading(false);
            }
        };
        if (eventId) init();
    }, [eventId]);

    const resetAll = () => {
        setMode(null);
        setStep(1);
        setSelectedStands([]);
        setStandiste(emptyPerson());
        setCompanyInfo({ companyName: "", chiefName: "", phone: "", email: "" });
    };

    const handleSelectMode = (chosen) => {
        setMode(chosen);
        setStep(1);
    };

    const toggleStand = (standId) => {
        setSelectedStands((prev) =>
            prev.includes(standId) ? prev.filter((id) => id !== standId) : [...prev, standId]
        );
    };

    const handleStep1Next = () => {
        if (!companyInfo.companyName || !companyInfo.chiefName || !companyInfo.phone || !companyInfo.email) {
            toast.error("Please fill all company onsite information.");
            return;
        }
        if (selectedStands.length === 0) {
            toast.error("Please select at least one stand.");
            return;
        }

        setStandiste((prev) => ({
            ...prev,
            name: companyInfo.chiefName || "",
            phone: companyInfo.phone || "",
            email: companyInfo.email || "",
        }));

        setStep(2);
    };

    const handleStandisteChange = (field, value) => {
        setStandiste((prev) => ({
            ...prev,
            [field]: value,
            verification: (field === "name" || field === "cin")
                ? { status: "idle", cinMatch: null, nameMatch: null }
                : prev.verification,
        }));
        if (field === "name" || field === "cin") scheduleVerification();
    };

    const handleStandistePhoto = async (side, file) => {
        if (!file) return;
        try {
            const compressed = await compressCINImage(file);
            setStandiste((prev) => ({
                ...prev,
                cinPhotos: { ...prev.cinPhotos, [side]: compressed },
                verification: side === "recto"
                    ? { status: "idle", cinMatch: null, nameMatch: null }
                    : prev.verification,
            }));
            if (side === "recto") scheduleVerification();
        } catch {
            toast.error("Could not process image. Use JPEG or PNG.");
        }
    };

    useEffect(() => {
        if (step === 2 && mode === "new") {
            setStandiste(prev => ({
                ...prev,
                name: companyInfo.chiefName,
                phone: companyInfo.phone,
                email: companyInfo.email,
            }));
        }
    }, [companyInfo, step, mode]);

    const startCamera = async (target) => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }
            });
            setStream(mediaStream);
            setCameraTarget(target);
            setTimeout(() => {
                if (videoRef.current) videoRef.current.srcObject = mediaStream;
            }, 100);
        } catch (err) {
            toast.error("Camera access denied or not available");
        }
    };

    const capturePhoto = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const track = stream.getVideoTracks()[0];
        const settings = track.getSettings();
        const maxSide = 1200;
        let w = settings.width || 1920;
        let h = settings.height || 1080;
        const scale = Math.min(maxSide / w, maxSide / h, 1);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob((blob) => {
            const file = new File([blob], `cin-${cameraTarget.side}-${Date.now()}.jpg`, { type: "image/jpeg" });
            handleStandistePhoto(cameraTarget.side, file);
            stopCamera();
        }, "image/jpeg", 0.85);
    };

    const stopCamera = () => {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        setStream(null);
        setCameraTarget(null);
    };

    const handleSubmitNew = async () => {
        if (submitting) return;

        if (!standiste.name || !standiste.cin || !standiste.phone) {
            toast.error("Please fill all required fields for the Stand Manager.");
            return;
        }
        if (!standiste.cinPhotos.recto || !standiste.cinPhotos.verso) {
            toast.error("Please upload both CIN photos for the Stand Manager.");
            return;
        }
        if (standiste.verification.status === "checking") {
            toast.error("CIN verification in progress, please wait...");
            return;
        }
        if (standiste.verification.status !== "done" || !standiste.verification.cinMatch || !standiste.verification.nameMatch) {
            toast.error("CIN verification not confirmed — please correct the info or retry.");
            return;
        }

        try {
            setSubmitting(true);

            const standisteForm = new FormData();
            standisteForm.append("name", standiste.name);
            standisteForm.append("email", standiste.email);
            standisteForm.append("phone", standiste.phone);
            standisteForm.append("cin", standiste.cin);
            standisteForm.append("role", "standiste");
            standisteForm.append("event", eventId);
            selectedStands.forEach((id) => standisteForm.append("stands[]", id));
            standisteForm.append("cinPhotos", standiste.cinPhotos.recto);
            standisteForm.append("cinPhotos", standiste.cinPhotos.verso);

            const standisteRes = await axiosInstance.post("/auth/register", standisteForm, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const standisteId = standisteRes.data.user._id;

            const jobs = [];

            jobs.push(
                axiosInstance.post("/badge", {
                    standisteId,
                    type: badgeType,
                    stands: selectedStands,
                })
            );

            selectedStands.forEach((standId) => {
                jobs.push(
                    axiosInstance.put(`/stand/${standId}`, {
                        onsite: {
                            contractor: {
                                companyName: companyInfo.companyName,
                                chiefName: companyInfo.chiefName,
                                phone: companyInfo.phone,
                                email: companyInfo.email,
                            },
                        },
                    })
                );
            });

            await Promise.all(jobs);
            setShowSuccess(true);
            resetAll();

        } catch (err) {
            console.error("Submit error:", err.response?.status, err.response?.data);
            const isTimeout = err.code === "ECONNABORTED" || err.message?.includes("timeout");
            const isNetwork = err.message === "Network Error" || !err.response;
            const message =
                isTimeout || isNetwork
                    ? "Request took too long or connection was lost. Please check your connection and try again."
                    : err.response?.data?.message ||
                    err.response?.data?.error ||
                    err.message ||
                    "Submission failed.";
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-14 h-14 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Loading…</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 max-w-sm text-center">
                    <svg className="w-10 h-10 text-red-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-800 font-semibold">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6">
            <div className="max-w-5xl mx-auto">

                <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-900 rounded-2xl mb-3 shadow-md">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Badge Registration</h1>
                    <p className="text-gray-500 text-sm mt-1">Complete all steps to register your team</p>
                </div>

                {mode === null && (
                    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="bg-blue-900 px-6 py-5 text-center">
                            <h2 className="text-white font-bold text-lg">Register your team</h2>
                            <p className="text-blue-200 text-sm mt-1">Select stands, fill your details, and submit your badge</p>
                        </div>
                        <div className="p-6 flex justify-center">
                            <div className="max-w-md w-full">
                                <RegistrationOptionCard
                                    accent="blue"
                                    title="New Registration"
                                    description="Register your company — select stands, fill your details, and submit your badge."
                                    ctaLabel="Get started"
                                    onClick={() => handleSelectMode("new")}
                                    icon={
                                        <svg className="w-7 h-7 text-blue-900 group-hover:text-white transition" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                        </svg>
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}

                {mode === "new" && (
                    <>
                        <Steps current={step} />

                        {step === 1 && (
                            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="mb-6 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                                    <div className="bg-blue-900 px-6 py-4">
                                        <h3 className="text-white font-bold text-lg">Company Onsite Information</h3>
                                    </div>
                                    <div className="p-6 space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Field label="Company Name" required>
                                                <input type="text" value={companyInfo.companyName}
                                                    onChange={(e) => setCompanyInfo(prev => ({ ...prev, companyName: e.target.value }))}
                                                    className={inputCls} />
                                            </Field>
                                            <Field label="Stand Manager Name" required>
                                                <input type="text" value={companyInfo.chiefName}
                                                    onChange={(e) => setCompanyInfo(prev => ({ ...prev, chiefName: e.target.value }))}
                                                    className={inputCls} />
                                            </Field>
                                            <Field label="Phone (WhatsApp)" required>
                                                <PhoneInput international defaultCountry="MA"
                                                    value={companyInfo.phone}
                                                    onChange={(value) => setCompanyInfo(prev => ({ ...prev, phone: value }))}
                                                    className={inputCls} />
                                            </Field>
                                            <Field label="Email" required>
                                                <input type="email" value={companyInfo.email}
                                                    onChange={(e) => setCompanyInfo(prev => ({ ...prev, email: e.target.value }))}
                                                    className={inputCls} />
                                            </Field>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-blue-900 px-6 py-5">
                                    <h2 className="text-white font-bold text-lg">Select Your Stand(s)</h2>
                                    <p className="text-blue-200 text-sm mt-0.5">Choose all stands you are working at</p>
                                </div>
                                <div className="p-6">
                                    {availableStands.length === 0 ? (
                                        <p className="text-gray-500 text-sm text-center py-8">No stands available for this event.</p>
                                    ) : (
                                        <div className="space-y-4">
                                            <input type="text" placeholder="Search by exhibitor or stand number..."
                                                value={standSearch} onChange={(e) => setStandSearch(e.target.value)}
                                                className={inputCls} />
                                            <div className="max-h-80 overflow-y-auto border rounded-xl">
                                                {availableStands
                                                    .filter((stand) =>
                                                        `${stand.exhibitorName} ${stand.standNumber}`.toLowerCase().includes(standSearch.toLowerCase())
                                                    )
                                                    .map((stand) => {
                                                        const selected = selectedStands.includes(stand._id);
                                                        return (
                                                            <div key={stand._id} onClick={() => toggleStand(stand._id)}
                                                                className={`flex items-center justify-between px-4 py-3 border-b cursor-pointer transition ${selected ? "bg-blue-50 border-blue-200" : "hover:bg-gray-50"}`}>
                                                                <div>
                                                                    <p className="font-semibold text-sm text-gray-800">{stand.exhibitorName || "No exhibitor"}</p>
                                                                    <p className="text-xs text-gray-500">Stand {stand.standNumber || "—"}</p>
                                                                </div>
                                                                {selected && <span className="text-blue-900 font-bold text-sm">✓</span>}
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    )}

                                    {selectedStands.length > 0 && (
                                        <p className="text-blue-900 text-xs font-semibold mt-4 text-center">
                                            {selectedStands.length} stand{selectedStands.length > 1 ? "s" : ""} selected
                                        </p>
                                    )}

                                    <div className="flex gap-3 mt-6">
                                        <button onClick={resetAll}
                                            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-3.5 rounded-xl transition flex items-center justify-center gap-2">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                                            </svg>
                                            Back
                                        </button>
                                        <button onClick={handleStep1Next}
                                            className="flex-1 bg-blue-900 hover:bg-blue-800 text-white font-bold py-3.5 rounded-xl transition shadow flex items-center justify-center gap-2">
                                            Continue
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="space-y-5">
                                <PersonForm
                                    data={standiste}
                                    label="Stand Manager"
                                    onChange={(field, val) => handleStandisteChange(field, val)}
                                    onPhoto={(side, file) => handleStandistePhoto(side, file)}
                                    onCamera={startCamera}
                                    personType="standiste"
                                    index={null}
                                    badgeType={badgeType}
                                    verification={
                                        standiste.cinPhotos.recto && standiste.name.trim() && standiste.cin.trim()
                                            ? standiste.verification
                                            : null
                                    }
                                    canRecheck={
                                        !!standiste.cinPhotos.recto &&
                                        !!standiste.name.trim() &&
                                        !!standiste.cin.trim() &&
                                        standiste.verification.status !== "checking"
                                    }
                                    onRecheck={handleRecheck}
                                />
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <button onClick={() => setStep(1)}
                                            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-3.5 rounded-xl transition flex items-center justify-center gap-2">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                                            </svg>
                                            Back to Setup
                                        </button>
                                        <button onClick={handleSubmitNew} disabled={submitting}
                                            className={`flex-1 font-bold py-3.5 rounded-xl transition shadow-md flex items-center justify-center gap-2 ${submitting ? "bg-emerald-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700"} text-white`}>
                                            {submitting ? (
                                                <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Submitting...</>
                                            ) : (
                                                <><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>Submit Badge</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {stream && (
                <div className="fixed inset-0 bg-black/90 flex flex-col items-center justify-center z-50 p-4">
                    <div className="w-full max-w-lg">
                        <div className="bg-white rounded-t-2xl px-6 py-4 text-center">
                            <h3 className="font-bold text-gray-900">
                                Capture CIN — {cameraTarget?.side === "recto" ? "Front" : "Back"}
                            </h3>
                        </div>
                        <video ref={videoRef} autoPlay playsInline className="w-full bg-black" style={{ maxHeight: "55vh" }} />
                        <canvas ref={canvasRef} className="hidden" />
                        <div className="bg-white rounded-b-2xl px-6 py-5 flex gap-4">
                            <button onClick={capturePhoto}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 rounded-xl transition flex items-center justify-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                </svg>
                                Capture
                            </button>
                            <button onClick={stopCamera}
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3.5 rounded-xl transition flex items-center justify-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showSuccess && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
                        <div className="bg-emerald-600 px-6 py-8 flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 shadow-lg">
                                <svg className="w-9 h-9 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h2 className="text-white text-2xl font-bold">Submitted!</h2>
                            <p className="text-emerald-100 text-sm mt-2">Your badge has been successfully registered.</p>
                        </div>
                        <div className="px-6 py-5 space-y-3 text-center">
                            <p className="text-gray-500 text-sm">
                                Your submission is under review. Once we finish, you will receive your credentials by email to manage your staff/team and badges via our platform.
                            </p>
                            <button onClick={() => setShowSuccess(false)}
                                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl transition shadow">
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StandisteForm;